import useClarity from '@/hooks/useClarity';
import authSlice, { isSkipApiSelector } from '@/redux/slice/auth.slice';
import highlightPreviewSlice from '@/redux/slice/highlightPreview.slice';
import RenderRouter from '@/routes';
import { AppProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import { shared } from '@shoplinedev/appbridge';
import 'moment/min/locales';
import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { appBridge, config, isEmbedded } from './config';
import { CrispSegmentEnum, Plan } from './constants/enum';
import { PATH } from './constants/path';
import { convertNullToString } from './helpers';
import { stringify } from './helpers/string';
import useUserPlan from './hooks/useUserPlan';
import { apiCaller, locationApiCaller } from './redux/query';
import loadingSlice from './redux/slice/loading';
import settingsSlice from './redux/slice/settings.slice';

declare global {
  interface Window {
    $crisp: any;
  }
}

function App() {
  useClarity();
  const dispatch = useDispatch();
  const isSkip = useSelector(isSkipApiSelector);
  const dataSettings = apiCaller.useGeneralSettingsQuery(undefined, { skip: isSkip });
  const getShopInfo = apiCaller.useGetShopInfoQuery(undefined, { skip: isSkip });
  const { data: locationInfo } = locationApiCaller.useCheckIpLocationQuery();
  const navigate = useNavigate();
  const { isShopifyPlus, currentPlan } = useUserPlan();

  useEffect(() => {
    if (getShopInfo.data && getShopInfo.data.data) {
      dispatch(settingsSlice.actions.handleShopInfo(getShopInfo.data.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(getShopInfo.data)]);

  useEffect(() => {
    if (locationInfo) {
      dispatch(settingsSlice.actions.handleLocationInfo(locationInfo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(locationInfo)]);

  useEffect(() => {
    const oldCrisp = document.getElementById('estimated-shopline-crisp');
    if (oldCrisp) return;

    // if (config && isEmbedded) {
    if (config) {
      //[tech:debt]: check embed
      const timer = setTimeout(() => {
        var crisp = document.createElement('script');
        crisp.setAttribute('src', `${process.env.REACT_APP_API_END_POINT}public/script/plugin.js?shop=${config.shop}`);
        crisp.setAttribute('id', 'estimated-shopline-crisp');
        crisp.appendChild(document.createTextNode(``));
        document.body.appendChild(crisp);
      }, 3000);
      return () => {
        clearTimeout(timer);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.shop]);

  useEffect(() => {
    if (!window.$crisp) return;
    if (isShopifyPlus) {
      window.$crisp.push(['set', 'session:segments', [CrispSegmentEnum.VIPCustomers]]);
    } else if ([Plan.STANDARD, Plan.PRO].includes(currentPlan)) {
      window.$crisp.push(['set', 'session:segments', [CrispSegmentEnum.PremiumCustomers]]);
    } else {
      window.$crisp.push(['set', 'session:segments', [CrispSegmentEnum.NewCustomers]]);
    }
  }, [currentPlan, isShopifyPlus]);

  // Load affiliate fingerprint script
  useEffect(() => {
    // Chỉ load script khi có đủ thông tin shop
    if (getShopInfo.data?.data && config) {
      const shopInfo = getShopInfo.data.data;
      const appId = shopInfo.appId;
      const shopDomain = shopInfo.shop;
      const shopId = shopInfo.shopId;

      if (appId && shopDomain && shopId) {
        const scriptId = 'affiliate-fingerprint-script';

        // Kiểm tra xem script đã được load chưa
        if (!document.getElementById(scriptId)) {
          const script = document.createElement('script');
          script.id = scriptId;
          script.src = `https://partners.omegatheme.com/s/api/affiliates/script.js?appId=${appId}&shopDomain=${shopDomain}&shopId=${shopId}`;
          script.async = true;

          script.onload = () => {
            console.log('[Affiliate] Script loaded successfully');
          };

          script.onerror = () => {
            console.error('[Affiliate] Failed to load script');
          };

          document.head.appendChild(script);
        }
      }
    }
  }, [getShopInfo.data]);

  useEffect(() => {
    dispatch(loadingSlice.actions.handleIsLoadingGeneralSettings(dataSettings.isLoading));
    dispatch(loadingSlice.actions.handleIsFetchingGeneralSettings(dataSettings.isFetching));
  }, [dataSettings.isFetching, dataSettings.isLoading, dispatch]);

  useEffect(() => {
    if (dataSettings.data) {
      const data = convertNullToString({ ...dataSettings.data.data, themeStoreId: dataSettings.data.data.themeStoreId || -1 });

      dispatch(
        settingsSlice.actions.handleDataSettings({
          ...data,
          week_working_days: dataSettings.data.data.week_working_days
            ? JSON.parse(dataSettings.data.data.week_working_days) || {}
            : {},
          specific_day_off: dataSettings.data.data.specific_day_off
            ? JSON.parse(dataSettings.data.data.specific_day_off) || []
            : [],
        }),
      );
      dispatch(
        settingsSlice.actions.handleDataSettingsBackup({
          ...data,
          week_working_days: dataSettings.data.data.week_working_days
            ? JSON.parse(dataSettings.data.data.week_working_days) || {}
            : {},
          specific_day_off: dataSettings.data.data.specific_day_off
            ? JSON.parse(dataSettings.data.data.specific_day_off) || []
            : [],
        }),
      );
      if (data.displayOnboarding) {
        navigate(PATH.WELCOME);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dataSettings.data), JSON.stringify(dispatch), JSON.stringify(navigate)]);

  // Di chuyển logic getToken ra ngoài render
  useEffect(() => {
    if (isSkip) {
      const getToken = async () => {
        try {
          dispatch(authSlice.actions.handleLoading(true));
          const sessionToken = await shared.getSessionToken(appBridge);
          dispatch(
            authSlice.actions.handleToken({
              token: sessionToken,
              expiredTime: Date.now() + 60 * 1000,
              isLoading: false,
            }),
          );
        } catch (err) {
          dispatch(authSlice.actions.handleLoading(false));
        }
      };
      getToken();
    }
  }, [isSkip, dispatch]);

  useEffect(() => {
    document.addEventListener('click', () => {
      dispatch(highlightPreviewSlice.actions.handleClearHighlightItem());
    });
    return () => {
      document.removeEventListener('click', () => {
        dispatch(highlightPreviewSlice.actions.handleClearHighlightItem());
      });
    };
  }, [dispatch]);

  return (
    <AppProvider i18n={enTranslations}>
      {isEmbedded ? (
        <>
          <RenderRouter />
        </>
      ) : (
        <RenderRouter />
      )}
    </AppProvider>
  );
}

export default memo(App);
